// /////////////////////////////////////////////////////////////
// [ファイル] useAssistantAgents.ts
// [概要] Workspace アシスタントのカスタム定義 (Rule / Skill / Workflow / 取組み課題) と
//        グループ (定義を束ねる作業文脈の単位) を取得するフック。
//        共有範囲の判定はバックエンドが権威的に行うため、Firestore を直接
//        購読せず Node 関数 listMyAgents 経由で「本人 + 共有された」定義だけを取得する。
//        定義の登録・更新・削除は AI がツール経由で行うため read のみ。
//        グループの作成・編集・削除は画面から manageAgentGroup を呼ぶ (AI ツールでも可)。
// /////////////////////////////////////////////////////////////
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../firebase/AuthProvider';
import { getIdToken } from '../firebase/getIdToken';

const LIST_MY_AGENTS_URL =
  'https://asia-northeast1-plexiform-bot-455507-h3.cloudfunctions.net/listMyAgents';
const MANAGE_AGENT_GROUP_URL =
  'https://asia-northeast1-plexiform-bot-455507-h3.cloudfunctions.net/manageAgentGroup';
const MANAGE_AGENT_URL =
  'https://asia-northeast1-plexiform-bot-455507-h3.cloudfunctions.net/manageAgent';

export type AgentType = 'rule' | 'skill' | 'workflow' | 'issue';
export type AgentScope = 'personal' | 'shared';
export type IssueStatus = 'open' | 'in_progress' | 'done';

export interface AgentItem {
  id: string;
  type: AgentType;
  scope: AgentScope;
  user_id?: string; // 作成者 (personal は本人)
  shared_with?: string[]; // scope='shared' の共有メンバー email (作成者を含む)
  group_id?: string; // 所属グループ id。空・未設定は「共通」(全セッションで注入)
  name: string;
  description: string;
  body: string;
  enabled?: boolean;
  status?: IssueStatus; // type='issue' のみ
  updated_at?: number; // 最終更新 (エポックms)。画面編集の楽観ロックに送る
}

// 画面からの定義保存の入力 (manageAgent action=save)
export interface SaveAgentInput {
  id?: string;
  type: AgentType;
  scope: AgentScope;
  name: string;
  description?: string;
  body: string;
  enabled?: boolean;
  status?: IssueStatus;
  sharedWith?: string[]; // 共通 (グループ無し) の shared のみ有効
  groupId?: string; // ''=共通 / id=グループ。省略時は現在値維持
  expectedUpdatedAt?: number; // 楽観ロック
}

/** 楽観ロック違反。latest に他者が保存した最新の定義が入る */
export class AgentConflictError extends Error {
  readonly latest: AgentItem;
  constructor(message: string, latest: AgentItem) {
    super(message);
    this.name = 'AgentConflictError';
    this.latest = latest;
  }
}

export interface AgentBuckets {
  rules: AgentItem[];
  skills: AgentItem[];
  workflows: AgentItem[];
  issues: AgentItem[];
}

// グループ (定義を束ねる作業文脈の単位)。共有はグループ単位で、中の定義はグループの共有先を継承する
export interface AgentGroup {
  id: string;
  scope: AgentScope;
  user_id: string; // 作成者
  shared_with: string[]; // scope='shared' の共有メンバー email (作成者を含む)
  name: string;
  description: string;
  enabled: boolean;
}

export interface SaveGroupInput {
  id?: string; // 更新時のみ
  scope: AgentScope;
  name: string;
  description?: string;
  enabled?: boolean;
  sharedWith?: string[]; // scope='shared' の共有相手 email。更新で省略時は現在値維持
}

const EMPTY: AgentBuckets = { rules: [], skills: [], workflows: [], issues: [] };

/** 定義の所属グループが「共通」かどうか (group_id 無し・空) */
export const isCommonAgent = (a: Pick<AgentItem, 'group_id'>): boolean => !a.group_id;

export const useAssistantAgents = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState<AgentBuckets>(EMPTY);
  const [groups, setGroups] = useState<AgentGroup[]>([]);
  // 通常チャットの基本プロンプトの文字数 (定義とは別枠で常に送られる量)。エージェント設定のメーター注記に使う。
  // 旧バックエンド応答 (未返却) では null のまま
  const [basePromptChars, setBasePromptChars] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  // 本人の定義・グループを 1 回でも取得し終えたか (成功・失敗とも)。ログイン情報が届く前の
  // 「user 無し」で loading が一瞬 false になる時は立てない (一覧が未グループのまま描かれるのを防ぐ)
  const [loaded, setLoaded] = useState(false);

  const refetch = useCallback(async () => {
    if (!user?.email) {
      setAgents(EMPTY);
      setGroups([]);
      setLoading(false);
      return;
    }
    // 再取得中もローディング表示できるよう毎回立てる (設定ダイアログを開いた時の再取得など)
    setLoading(true);
    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await axios.post(
        LIST_MY_AGENTS_URL,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'success') {
        // 旧バックエンド応答 (issues 無し) でも各バケットが undefined にならないよう正規化する
        setAgents({ ...EMPTY, ...(res.data.agents ?? {}) });
        setGroups(Array.isArray(res.data.groups) ? (res.data.groups as AgentGroup[]) : []);
        setBasePromptChars(typeof res.data.basePromptChars === 'number' ? res.data.basePromptChars : null);
      }
    } catch (err) {
      console.error('Agent 定義の取得に失敗しました', err);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [user?.email]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // グループの保存 (新規/更新)。失敗時はバックエンドのメッセージで例外にする (呼び出し側が通知)
  const saveGroup = useCallback(
    async (input: SaveGroupInput): Promise<void> => {
      const token = await getIdToken();
      if (!token) throw new Error('認証トークンを取得できませんでした');
      const res = await axios.post(
        MANAGE_AGENT_GROUP_URL,
        { action: 'save', ...input },
        { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true }
      );
      if (res.data?.status !== 'success') {
        throw new Error(res.data?.message ?? 'グループの保存に失敗しました');
      }
      await refetch();
    },
    [refetch]
  );

  // グループの削除。定義が残っているグループはバックエンドが拒否する
  const deleteGroup = useCallback(
    async (id: string): Promise<void> => {
      const token = await getIdToken();
      if (!token) throw new Error('認証トークンを取得できませんでした');
      const res = await axios.post(
        MANAGE_AGENT_GROUP_URL,
        { action: 'delete', id },
        { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true }
      );
      if (res.data?.status !== 'success') {
        throw new Error(res.data?.message ?? 'グループの削除に失敗しました');
      }
      await refetch();
    },
    [refetch]
  );

  // manageAgent を呼ぶ共通処理。409 (楽観ロック違反) は AgentConflictError にして最新を渡す
  const callManageAgent = useCallback(
    async (payload: Record<string, unknown>): Promise<void> => {
      const token = await getIdToken();
      if (!token) throw new Error('認証トークンを取得できませんでした');
      const res = await axios.post(MANAGE_AGENT_URL, payload, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: () => true,
      });
      if (res.status === 409 && res.data?.status === 'conflict') {
        throw new AgentConflictError(res.data.message ?? '他の利用者が先に更新しました', res.data.latest);
      }
      if (res.data?.status !== 'success') {
        throw new Error(res.data?.message ?? '定義の更新に失敗しました');
      }
      await refetch();
    },
    [refetch]
  );

  // 定義の保存 (画面の編集モード)。本文の書き込みは AI ツールと同じストア関数を通る
  const saveAgent = useCallback(
    (input: SaveAgentInput) => callManageAgent({ action: 'save', ...input }),
    [callManageAgent]
  );
  // 所属グループの付け替えのみ (本文に触れない)
  const assignAgentGroup = useCallback(
    (id: string, groupId: string, expectedUpdatedAt?: number) =>
      callManageAgent({ action: 'assign', id, groupId, expectedUpdatedAt }),
    [callManageAgent]
  );
  const deleteAgent = useCallback(
    (id: string) => callManageAgent({ action: 'delete', id }),
    [callManageAgent]
  );

  return {
    agents,
    groups,
    basePromptChars,
    loading,
    loaded,
    refetch,
    saveGroup,
    deleteGroup,
    saveAgent,
    assignAgentGroup,
    deleteAgent,
  };
};
